import { API, Auth } from 'aws-amplify';
import ReactGA from 'react-ga4';
import gtmService from '../../services/GTMService';

import {
  ADMINISTRATOR_USER,
  ASSOCIATION_USER,
  INSTITUTION_USER,
  REPORT_USER,
  USER_TOKEN,
  USER_EMAIL,
  LOGGED_USER_DETAILS,
  USERS_LIST,
  USERS_LIST_LOADING,
  RESET_USERS_LIST,
  USER_LOGOUT,
  PREVIEW_USER,
  PILLSBUGS_ACTION,
  PILLSBUGS_SET_ITEM_STATE,
  PILLSBUGS_RESET,
  SEARCH_CAS,
  SEARCH_INSTITUTIONS,
  SEARCH_ORGANIZATION,
  SEARCH_STATUSES,
  SEARCH_USERROLES,
  SEARCH_FILTER_PANEL_TOGGLE,
  USERS_RESET,
  RESET_SESSION,
  USER_SET_INSTITUTION,
  PREVIEW_USER_RESET,
  USER_SET_ACTIVE_CAS,
  SUPER_USER,
  USER_SET_INSTITUTION_LOADING,
  ORAGNIZATION_LIST_BY_CLIENT_ID,
  ASSOCIATION_SPECIALTY,
  ASSOCIATION_SPECIALTY_LIST,
} from '../../actionTypes/Users/UserActionType';
import Api from '../../Api';
import Constants from '../../Constants';
import { resetPageState } from '../Page/PageAction';
import { onErrorMsg } from '../FlashMessage/FlashMessages';
import { dynamicSort } from '../../utils/helpers';
import { createUsersSearchPayload } from '../../utils/search';
import { debounce } from '@mui/material';
import { resetUserImpersonateState } from './ImpersonateAction';

const api = new Api();

export const resetUsersState = () => {
  return (dispatch) => {
    dispatch({
      type: USERS_RESET,
    });
  };
};

export const resetPillsBugs = () => {
  return (dispatch) => {
    dispatch({ type: PILLSBUGS_RESET });
  };
};

export const resetUsersList = () => {
  return (dispatch) => {
    dispatch({ type: RESET_USERS_LIST });
  };
};

export const resetUserPreview = () => {
  return { type: PREVIEW_USER_RESET };
};

export const resetSessionState = () => {
  return (dispatch) => {
    dispatch({ type: RESET_SESSION });
  };
};

function getInitialStateSuccess() {
  return {
    type: USER_LOGOUT,
  };
}

export const getResetState = () => {
  return (dispatch) => {
    dispatch(getInitialStateSuccess());
  };
};

function getUserTokenSuccess(token) {
  return {
    type: USER_TOKEN,
    token: token,
  };
}

export const getUserToken = () => {
  return async (dispatch) => {
    try {
      const data = await Auth.currentSession();
      dispatch(getUserTokenSuccess(data.idToken.jwtToken));
    } catch (err) {
      console.error(err);
      dispatch(getUserTokenSuccess(''));
    }
  };
};

export function cleanUserToken() {
  return (dispatch) => {
    dispatch(getUserTokenSuccess(''));
  };
}

function getUserEmailSuccess(email) {
  return {
    type: USER_EMAIL,
    email: email,
  };
}

export function setLoggedUserDetailsSuccess(userDetails) {
  return {
    type: LOGGED_USER_DETAILS,
    payload: userDetails,
  };
}

export function setUserInstitutionData(userInstitutionData) {
  return {
    type: USER_SET_INSTITUTION,
    payload: userInstitutionData,
  };
}

export function setUserInstitutionDataLoading(state = false) {
  return {
    type: USER_SET_INSTITUTION_LOADING,
    payload: state,
  };
}

export function setSessionUserActiveCAS(casDetails) {
  return (dispatch) =>
    dispatch({
      type: USER_SET_ACTIVE_CAS,
      payload: casDetails,
    });
}

export const fetchSessionUserInstitutions = () => {
  return async (dispatch, getStateFn) => {
    const state = getStateFn();
    const isSuperUser = state.AppSession.userDetails.userRole === SUPER_USER;
    try {
      dispatch(setUserInstitutionDataLoading(true));
      // we get active user's CAS + InstitutionID
      const userInstitutionData = isSuperUser ? {} : await api.getData(Constants.apiUrls.userInstitution);
      dispatch(setUserInstitutionData(userInstitutionData));
    } catch (e) {
      console.error(e);
      dispatch(onErrorMsg('Not loaded data!'));
    }
    dispatch(setUserInstitutionDataLoading(false));
  };
};

export const fetchSessionUserAvailableCASes = async (userInstitutionId) => {
  try {
    const resp = await api.getData(`${Constants.apiUrls.casOrganizationsData}/${userInstitutionId}`);
    return resp;
  } catch (e) {
    console.error(e);
  }
  return [];
};

export const fetchUser = async (guid) => {
  const url = Constants.apiUrls.usersList + '/' + guid;
  return guid ? api.getData(url) : void 0;
};

export const getUserDetailsFromSession = async () => {
  const { signInUserSession } = await Auth.currentAuthenticatedUser();
  const { payload } = signInUserSession.idToken;
  return {
    given_name: payload.given_name,
    family_name: payload.family_name,
    userRole: payload['custom:userRole'],
    userRoleConstant: payload['custom:userRole'],
    email: payload.email,
    phone_number: payload.phone_number,
    userPrincipalId: payload['custom:userPrincipalId'],
    userManagementAccess: payload['custom:userManagementAccess'],
  };
};

export const getUserEmail = (guid, respCall) => {
  const myInit = {
    queryStringParameters: {
      guid: guid,
    },
  };
  return (dispatch) => {
    API.get('AnalyticsAPI', Constants.apiUrls.userEmail, myInit)
      .then((resp) => {
        dispatch(getUserEmailSuccess(resp));
        respCall(resp);
      })
      .catch(() => {});
  };
};

// Helper function to extract username from email (part before @)
const getUsernameFromEmail = (email) => {
  if (!email || email === 'unknown') return 'unknown';
  const atIndex = email.indexOf('@');
  return atIndex !== -1 ? email.substring(0, atIndex) : email;
};

export const sendGADimensions = async (
  dimension1 = 'N/A',
  dimension2 = 'N/A',
  dimension3 = 'N/A',
  dimension4 = 'N/A',
) => {
  let gaDimensions = {
    dimension1: 'N/A',
    dimension2: 'N/A',
    dimension3: 'N/A',
    dimension4: 'N/A',
  };
  const lastActiveDate = new Date().toISOString().split('T')[0];
  try {
    const userDetails = await getUserDetailsFromSession();
    switch (userDetails.userRole) {
      case ASSOCIATION_USER:
        gaDimensions = { ...gaDimensions, dimension1, dimension2 };
        break;
      case ADMINISTRATOR_USER:
      case REPORT_USER:
        gaDimensions = { dimension1, dimension2, dimension3, dimension4 };
        break;
      case INSTITUTION_USER:
        gaDimensions = { ...gaDimensions, dimension1, dimension4 };
        break;
      default:
        gaDimensions = { ...gaDimensions, dimension1 };
    }
    ReactGA.initialize('JSZ67SJ7CN');
    ReactGA.set({
      user_properties: {
        user_role: userDetails.userRole || 'unknown',
        user_username: getUsernameFromEmail(userDetails.email),
        user_id: userDetails.userPrincipalId || 'unknown',
        user_cas_name: dimension2 || 'unknown',
        user_client_name: dimension3 || 'unknown',
        last_active_date: lastActiveDate
      }
    });
    const eventParams = {
      user_role: userDetails.userRole || 'unknown',
      user_username: getUsernameFromEmail(userDetails.email),
      user_id: userDetails.userPrincipalId || 'unknown',
      user_cas_name: dimension2 || 'unknown',
      user_client_name: dimension3 || 'unknown',
      last_active_date: lastActiveDate
    };
    ReactGA.event('custom_dimensions_event', eventParams);
    
    // GTM tracking (new) - send the same user dimensions
    const gtmUserDimensions = {
      user_id: userDetails.userPrincipalId || 'unknown',
      user_role: userDetails.userRole || 'unknown',
      user_username: getUsernameFromEmail(userDetails.email),
      user_cas_name: dimension2 || 'unknown',
      user_client_name: dimension3 || 'unknown',
      user_institution_name: dimension4 || 'unknown',
      last_active_date: lastActiveDate,
      // Include GA4 dimension mapping for compatibility
      dimension1,
      dimension2,
      dimension3,
      dimension4
    };
    
    gtmService.trackUserDimensions(gtmUserDimensions);
    
  } catch (err) {
    console.error(err);
  }
  //ReactGA.set(gaDimensions);
  // Optionally set user properties globally (e.g. user role)

  

  // Send a custom event with the dimensions as event parameters
  // ReactGA.event('custom_dimensions_event', gaDimensions);
};

export const logoutAccount = () => {
  return async (dispatch) => {
    try {
      await Auth.signOut();
      //clearing session and local storage
      localStorage.clear();
      sessionStorage.clear();

      dispatch(resetSessionState());
      dispatch(resetPageState());
      dispatch(resetUsersState());
      dispatch(resetUserImpersonateState());
    } catch (err) {
      console.error(err);
    }
  };
};

export function getUsersListSuccess(usersList) {
  return {
    type: USERS_LIST,
    usersList: usersList,
  };
}

export function setUsersListLoading() {
  return {
    type: USERS_LIST_LOADING,
  };
}

export const getDefaultUsersList = () => {
  return (dispatch, getState) => {
    const defaultSearchParams = createUsersSearchPayload();
    getUsersList(defaultSearchParams)(dispatch, getState);
  };
};

const fetchUsersList = debounce(async (queryParams, currentSearchParams, dispatch) => {
  const url = Constants.apiUrls.search;
  const reqData = {
    body: {
      limit: queryParams.pageSize,
      page: queryParams.pageNumber,
      terms: {
        ...queryParams.terms,
        internalUser:
          queryParams.terms.internalUser && queryParams.terms.internalUser.length > 0
            ? queryParams.terms.internalUser[0] === 'true' || null
            : null,
      },
    },
  };
  try {
    const resp = await api.postData(url, reqData);
    if (queryParams.type === 'up') {
      resp.contents.sort(dynamicSort(queryParams.name));
    } else if (queryParams.type === 'down') {
      resp.contents.sort(dynamicSort('-' + queryParams.name));
    }
    const data = {
      ...resp,
      name: queryParams.name,
      type: queryParams.type,
      terms: queryParams.terms,
    };
    dispatch(getUsersListSuccess(data));
  } catch (err) {
    console.error(err);
    dispatch(
      getUsersListSuccess({
        ...currentSearchParams,
        name: queryParams.name,
        type: queryParams.type,
        terms: queryParams.terms,
      }),
    );
  }
}, 1000);

export const getUsersList = (searchParams = {}) => {
  return async (dispatch, getStateFn) => {
    const state = getStateFn();
    const currentSearchParams = state.Users.usersList;

    const queryParams = createUsersSearchPayload({
      ...currentSearchParams,
      ...(searchParams || {}),
      terms: {
        ...currentSearchParams.terms,
        ...(searchParams ? searchParams.terms || {} : {}),
      },
    });

    // TODO: we pre-set selected terms to display them instantly
    dispatch(
      getUsersListSuccess({
        ...currentSearchParams,
        name: queryParams.name,
        type: queryParams.type,
        terms: queryParams.terms,
      }),
    );

    dispatch(setUsersListLoading());
    fetchUsersList(queryParams, currentSearchParams, dispatch);
  };
};

export const getAddUserFormHeader = (userRole) => {
  switch (userRole) {
    case ADMINISTRATOR_USER:
      return 'Add Report User';
    case INSTITUTION_USER:
      return 'Add Institution User';
    case ASSOCIATION_USER:
      return 'Add Association User';
    default:
      return 'Add User';
  }
};

export function setPreviewUser(userDetails) {
  return { type: PREVIEW_USER, payload: userDetails };
}

export const fetchPreviewUser = (guid) => {
  return async (dispatch) => {
    try {
      const result = await fetchUser(guid);
      dispatch(setPreviewUser(result));
      return result;
    } catch (err) {
      const errorMsg = err.response ? err.response.data.error : '';
      dispatch(onErrorMsg(errorMsg));
      return err;
    }
  };
};

function setPillsBugsSuccess(pillsBugs) {
  return {
    type: PILLSBUGS_ACTION,
    pillsBugs: pillsBugs,
  };
}

export const setPillsBugs = (pillsBugs) => {
  return (dispatch) => {
    dispatch(setPillsBugsSuccess(pillsBugs));
  };
};

export const setPillState = (name, state = false) => {
  return (dispatch) => {
    dispatch({
      type: PILLSBUGS_SET_ITEM_STATE,
      payload: {
        name,
        state,
      },
    });
  };
};

function getCasSuccess(cas) {
  return {
    type: SEARCH_CAS,
    cas: cas,
  };
}

function setSearchInstitutions(institutions) {
  return { type: SEARCH_INSTITUTIONS, payload: { institutions } };
}

function getOrgsSuccess(orgs) {
  return {
    type: SEARCH_ORGANIZATION,
    orgs: orgs,
  };
}

function getStatusSuccess(statuses) {
  return {
    type: SEARCH_STATUSES,
    statuses: statuses,
  };
}

function getUserRolesSuccess(userRoles) {
  return {
    type: SEARCH_USERROLES,
    userRoles: userRoles,
  };
}

export const openSearchFilterPanel = () => {
  return {
    type: SEARCH_FILTER_PANEL_TOGGLE,
    payload: {
      filterPanelOpen: true,
    },
  };
};

export const closeSearchFilterPanel = () => {
  return {
    type: SEARCH_FILTER_PANEL_TOGGLE,
    payload: {
      filterPanelOpen: false,
    },
  };
};

export const fetchSearchInstitutions = () => {
  return async (dispatch) => {
    const url = Constants.apiUrls.searchInstitutions;
    try {
      const institutions = await api.getData(url);
      dispatch(setSearchInstitutions(institutions));
    } catch (e) {
      dispatch(onErrorMsg('Not loaded data!'));
    }
  };
};

export const getUserRolesSearch = () => {
  return (dispatch) => {
    const url = Constants.apiUrls.searchUserRoles;
    api.getData(url).then((resp) => {
      dispatch(getUserRolesSuccess(resp));
    });
  };
};

export const getStatusesSearch = () => {
  return (dispatch) => {
    const url = Constants.apiUrls.searchStatuses;
    api.getData(url).then((resp) => {
      dispatch(getStatusSuccess(resp));
    });
  };
};

export const getOrgsSearch = () => {
  return (dispatch) => {
    const url = Constants.apiUrls.searchOrgs;
    api.getData(url).then((resp) => {
      dispatch(getOrgsSuccess(resp));
    });
  };
};

export const getCasSearch = () => {
  return (dispatch) => {
    const url = Constants.apiUrls.searchCas;
    api.getData(url).then((resp) => {
      dispatch(getCasSuccess(resp));
    });
  };
};

export const getOrganizationsByClientId = (clientId) => {
  return (dispatch) => {
    const url = Constants.apiUrls.organizationList + '?casId=' + clientId;
    api.getData(url).then((resp) => {
      dispatch(getOrganizationsByClientIdSuccess(resp));
    });
  };
};
function getOrganizationsByClientIdSuccess(resp) {
  return {
    type: ORAGNIZATION_LIST_BY_CLIENT_ID,
    payload: {
      organizationListByClientId: resp,
    },
  };
}

export const getAssociationSpecialtyById = (associationId) => {
  return (dispatch) => {
    const url = `${Constants.apiUrls.associationSpecialty}/${associationId}`;
    api.getData(url).then((resp) => {
      dispatch(getAssociationSpecialtyByIdSuccess(resp));
    });
  };
};

export const getAssociationSpecialty = () => {
  return (dispatch) => {
    const url = `${Constants.apiUrls.associationSpecialty}`;
    api.getData(url).then((resp) => {
      dispatch(getAssociationSpecialtySuccess(resp));
    });
  };
};
function getAssociationSpecialtySuccess(resp) {
  return {
    type: ASSOCIATION_SPECIALTY_LIST,
    payload: {
      associationSpecialtyList: resp,
    },
  };
}
function getAssociationSpecialtyByIdSuccess(resp) {
  return {
    type: ASSOCIATION_SPECIALTY,
    payload: {
      associationSpecialty: resp,
    },
  };
}
